import { Company } from './../types/company';
import { AssigneeSuggestion, DraftRoutePlan, DraftRouteStop, Job, JobPattern, LocationInsights } from "../types/job";
import { PlaceSuggestion } from "../types/place";
import { Md5 } from 'ts-md5';
import { getRecoil } from '../providers/RecoilAccessProvider';
import { passwordState, tokenState, uidState, usernameState } from '../states/accountState';
import { markSkillListStale } from '../states/skillListState';
import { Member } from "../types/member";
import { Message } from "../types/message";
import { AreaOutline, Zone } from "../types/zone";
import { ProjectedOccurrence, ReadableSequence, SavableSequence, SequenceDayUpdate } from "../types/sequence";
import { WeekPlan, WeekPlanApply } from "../types/weekPlan";
import { FormContent, FormTemplate } from "../types/form";
import { dayOfToday } from "../utils/time";
import { SortConfig } from '../types/jobFilters';
import { AlertConfig, AlertInstance } from "../types/alerts";
import { TimesheetEntry } from "../types/timesheet";
import { RecurringReportConfig, ReportJob, UploadedReport } from "../types/reports";
import { Day, Latitude, LatLng, Longitude, Timestamp, Uid, UidDay } from "../types/core";
import {
    AttentionItem,
    AttentionMute,
    normalizeDistributions,
    normalizeMemberStatDays,
    normalizeMemberStats,
    normalizePlaceStats,
    normalizeFormStats,
    normalizeVisitStats,
    VisitSplit,
    UsageSignals
} from "../types/stats";
import { Activity } from "../types/notification";
import { Cluster } from "../types/cluster";
import { isTestEnvironment } from "../utils/app";
import { Check } from "../types/checks";
import { ApprovalStatus, TrackEntry } from "../types/track";
import { Trip } from "../types/trip";
import { LogEvent } from "../types/logevent";
import { RoutingMatrix } from "../types/routing";
import { JobAssigneeFacts } from "../types/assigneeFacts";
import { SkillEvidence, SkillListEntry } from "../types/skillList";
import { VoizReportSubmission } from "../types/voizReport";
import { JobGroup } from "../types/jobGroup";
import { ExternalIntegrationOverview, ExternalIntegrationProvider } from "../types/externalIntegration";
import { MemberLiveBadge } from "../types/memberLiveBadge";

type Mode = "PROD" | "QA" | "LOCAL";

export const MODE: Mode = isTestEnvironment() ? "QA" : "PROD";
export const PROD_DOMAIN = 'live.hellotracks.com';
export const QA_DOMAIN = 'qa.hellotracks.com';
export const LOCAL_DOMAIN = 'localhost:8766';
export const HTTPS_DOMAIN = "https://hellotracks.com";

// @ts-ignore
const SCHEMA = MODE === "LOCAL" ? "http://" : "https://";
// @ts-ignore
export const API_DOMAIN = MODE === "LOCAL" ? LOCAL_DOMAIN : MODE === "QA" ? QA_DOMAIN : PROD_DOMAIN;
export const API_ENDPOINT = SCHEMA + API_DOMAIN + "/api";

export const STATUS_OK = 0;
export const STATUS_ERROR_USERUNKNOWN = -2;
export const STATUS_ERROR_PASSWORDMISMATCH = -3;
export const STATUS_ERROR_USERALREADYEXISTS = -4;
export const STATUS_ERROR_NOPERMISSION = -5;
export const STATUS_ERROR_NOTREADY = -7;
export const STATUS_ERROR_THROTTLING = -9;
export const STATUS_ERROR_FORCELOGOUT = -12;
export const STATUS_ERROR_NETWORK = -999;

export enum Endpoint {
    autopilotAnalyze = 'autopilot/analyze',
    autopilotState = 'autopilot/state',
    autopilotActivation = 'autopilot/activation',
    autopilotPolicyGet = 'autopilot/policy/get',
    autopilotPolicySave = 'autopilot/policy/save',
    autopilotAutonomy = 'autopilot/autonomy',
    autopilotDecisionResolve = 'autopilot/decision/resolve',
    autopilotDecisionExecute = 'autopilot/decision/execute',
    autopilotDecisionUndo = 'autopilot/decision/undo',
    autopilotIntakeSubmit = 'autopilot/intake/submit',
    autopilotIntakeConfirm = 'autopilot/intake/confirm',
    autopilotIntakeUndo = 'autopilot/intake/undo',
    autopilotIntakeDismiss = 'autopilot/intake/dismiss',
    autopilotGoalSubmit = 'autopilot/goal/submit',
    autopilotGoalApply = 'autopilot/goal/apply',
    autopilotGoalRecheck = 'autopilot/goal/recheck',
    autopilotGoalKeep = 'autopilot/goal/keep',
    autopilotGoalUndo = 'autopilot/goal/undo',
    autopilotGoalDismiss = 'autopilot/goal/dismiss',
    autopilotGoalPlan = 'autopilot/goal/plan',
    autopilotGoalImage = 'autopilot/goal/image',
    autopilotGoalAnswer = 'autopilot/goal/answer',
    weblogin = 'weblogin',
    getaccounts = 'getaccounts',
    getaccountuids = 'getaccountuids',
    getjobs = 'getjobs',
    etasessions = 'etasessions',
    getjobgroups = 'getjobgroups',
    createaccount = 'createaccount',
    editaccount = 'editaccount',
    changeusername = 'changeusername',
    deleteaccount = 'deleteaccount',
    deleteteam = 'deleteteam',
    deleteplaces = 'deleteplaces',
    editjobs = 'editjobs',
    createjobs = 'createjobs',
    createjobgroups = 'createjobgroups',
    clonejobs = 'clonejobs',
    deletejobs = 'deletejobs',
    uploadprofileimage = 'uploadprofileimage',
    uploadjobattachment = 'uploadfile/jobattachment',
    uploadformattachment = 'uploadfile/formattachment',
    uploadapplog = 'uploadfile/applog',
    uploadaudiotranscription = 'uploadfile/audiotranscription',
    uploadgoalimage = 'uploadfile/goalimage',
    uploadjobimport = 'uploadfile/jobimport',
    uploadjobimportcheck = 'uploadfile/jobimportcheck',
    uploadsequenceimport = 'uploadfile/jobsequenceimport',
    uploadzoneimport = 'uploadfile/zoneimport',
    uploadplaceimport = 'uploadfile/placeimport',
    uploadmemberimport = 'uploadfile/memberimport',
    importstatus = 'importstatus',
    importlayouts = 'importlayouts',
    optimizeroute = 'optimizeroute',
    calculateroute = 'calculateroute',
    optimizemultiroute = 'optimizemultiroute',
    reorderroute = 'reorderroute',
    placesautocomplete = 'placesautocomplete',
    assignjob = 'assignjob',
    geocode = 'geocode',
    savezone = 'savezone',
    deletezone = 'deletezone',
    loadsequences = 'loadsequences',
    savesequence = 'savesequence',
    applyrecurringsequences = 'applyrecurringsequences',
    clearrecurringsequences = 'clearrecurringsequences',
    editsequencejob = 'editsequencejob',
    projectsequences = 'projectsequences',
    materializesequence = 'materializesequence',
    updatesequencejobs = 'updatesequencejobs',
    planweek = 'planweek',
    confirmweekplan = 'confirmweekplan',
    getjobtemplates = 'getjobtemplates',
    getrecentjobpatterns = 'getrecentjobpatterns',
    getlocationinsights = 'getlocationinsights',
    getplacesuggestions = 'getplacesuggestions',
    areaoutlines = 'areaoutlines',
    suggestassignees = 'suggestassignees',
    draftroute = 'draftroute',
    routeundo = 'routeundo',
    geolocateplaces = "geolocateplaces",
    assignsequence = 'assignsequence',
    loadformsubmissions = 'loadformsubmissions',
    loadvoizreports = 'loadvoizreports',
    loadpublicformsubmissions = 'loadpublicformsubmissions',
    loadformtemplates = 'loadformtemplates',
    saveformtemplate = 'saveformtemplate',
    archivejobs = 'archivejobs',
    restorejobs = 'restorejobs',
    editjobgroups = 'editjobgroups',
    deletejobgroups = 'deletejobgroups',
    savejobastemplate = 'savejobastemplate',
    notifyroutechange = 'notifyroutechange',
    conversations = 'conversations',
    getrecapconfig = 'getrecapconfig',
    saverecapconfig = 'saverecapconfig',
    createflyoverclip = 'createflyoverclip',
    renderflyoverclip = 'renderflyoverclip',
    listflyoverclips = 'listflyoverclips',
    editflyoverclip = 'editflyoverclip',
    deleteflyoverclip = 'deleteflyoverclip',
    shareflyoverclip = 'shareflyoverclip',
    flyoverevents = 'flyoverevents',
    flyoverdays = 'flyoverdays',
    conversation = 'conversation',
    sendmsg = 'sendmsg',
    delmsg = 'delmsg',
    getbilling = 'getbilling',
    setcompanyeactive = 'setcompanyactive',
    deactivationhelp = 'deactivationhelp',
    getalertinstances = 'getalertinstances',
    getalertconfigs = 'getalertconfigs',
    savealertconfig = 'savealertconfig',
    getstops = 'getstops',
    gettimesheet = 'gettimesheet',
    createreport = 'createreport',
    loadreportjob = 'loadreportjob',
    loadreportjobs = 'loadreportjobs',
    requestpassword = 'requestpassword',
    starttotpsetup = 'starttotpsetup',
    verifytotpsetup = 'verifytotpsetup',
    disabletotp = 'disabletotp',
    regeneratebackupcodes = 'regeneratebackupcodes',
    startverifyemail = 'startverifyemail',
    verifyemailcode = 'verifyemailcode',
    startverifyphone = 'startverifyphonel',
    editinvitecode = 'editinvitecode',
    createinvitecode = 'createinvitecode',
    sendgooglecalendarinvite = 'sendgooglecalendarinvite',
    resetfield = 'resetfield',
    disconnectslack = "disconnectslack",
    editplace = 'editplace',
    createplace = 'createplace',
    applyplacestopastvisits = 'applyplacestopastvisits',
    loaduploadedreports = 'loaduploadedreports',
    loadrecurringreportconfigs = 'loadrecurringreportconfigs',
    saverecurringreportconfig = 'saverecurringreportconfig',
    createcompany = 'createcompany',
    setprofileimageinitials = 'setprofileimageinitials',
    startroutesimulation = 'startroutesimulation',
    stoproutesimulation = 'stoproutesimulation',
    savetimesheetentry = 'savetimesheetentry',
    deletetracks = 'deletetracks',
    publishtracks = 'publishtracks',
    edittrack = "edittrack",
    createtrack = "createtrack",
    downloadtracks = 'downloadtracks',
    usernameavailable = 'usernameavailable',
    websignup = 'websignup',
    utmcollect = 'utm/collect',
    personalize = 'personalize',
    matchaiquery = "matchai/query",
    matchaibatch = "matchai/batch",
    matchaiskills = "matchai/skills",
    matchaiskilllist = "matchai/skilllist",
    matchaimemberskills = "matchai/memberskills",
    matchaiskillcandidates = "matchai/skillcandidates",
    matchaiskillrename = "matchai/skillrename",
    passwordrecovery = 'passwordrecovery',
    activation = 'activation',
    statsmembers = 'stats/members',
    statshistory = 'stats/history',
    statsattention = 'stats/attention',
    statsplaces = 'stats/places',
    reportingcatalog = 'reporting/catalog',
    reportingpreview = 'reporting/preview',
    reportingstart = 'reporting/start',
    reportingprogress = 'reporting/progress',
    reportingfiles = 'reporting/files',
    reportinglist = 'reporting/list',
    reportingstore = 'reporting/store',
    reportingremove = 'reporting/remove',
    reportingsendnow = 'reporting/sendnow',
    reportingswitch = 'reporting/switch',
    reportingcolsets = 'reporting/colsets',
    reportingsavecolset = 'reporting/savecolset',
    reportingdropcolset = 'reporting/dropcolset',
    statsforms = 'stats/forms',
    statsvisits = 'stats/visits',
    prepareaddpaymentmethod = 'prepareaddpaymentmethod',
    setdefaultpaymentmethod = "setdefaultpaymentmethod",
    getinvoices = "getinvoices",
    getplanprices = "getplanprices",
    changeplan = "changeplan",
    getactivities = "getactivities",
    resendinvite = "resendinvite",
    distributejobs = "distributejobs",
    submitform = "submitform",
    getchecks = "getchecks",
    getmemberlivebadges = "getmemberlivebadges",
    gettrips = "gettrips",
    getloghistory = "getloghistory",
    authgoogle = "auth/google",
    authturnstile = "auth/turnstile",
    routingmatrix = "routing/matrix",
    routingdirections = "routing/directions",
    getdailylicenses = "getdailylicenses",
    getwebhooks = "getwebhooks",
    setwebhooks = "setwebhooks",
    createapikey = "createapikey",
    getapikeys = "getapikeys",
    revokeapikey = "revokeapikey",
    deleteapikey = "deleteapikey",
    getresellerclients = "getresellerclients",
    createresellerclient = "createresellerclient",
    attachresellerclient = "attachresellerclient",
    updateresellerclient = "updateresellerclient",
    getresellerbilling = "getresellerbilling",
    createsupportsession = "createsupportsession",
    redeemsupportsession = "redeemsupportsession",
    getaffiliatestatus = "getaffiliatestatus",
    enrollaffiliate = "enrollaffiliate",
    setaffiliatecode = "setaffiliatecode",
    getaffiliateoverview = "getaffiliateoverview",
    getaffiliateclients = "getaffiliateclients",
    getaffiliatelink = "getaffiliatelink",
    regenerateaffiliatecode = "regenerateaffiliatecode",
    getexternalintegrations = "getexternalintegrations",
    saveexternalintegrationconfig = "saveexternalintegrationconfig",
    saveexternalintegrationmappings = "saveexternalintegrationmappings",
    startexternalintegrationconnect = "startexternalintegrationconnect",
    disconnectexternalintegration = "disconnectexternalintegration",
    reconcileexternalintegration = "reconcileexternalintegration",
}

export function getAuthObj() {
    const uid = getRecoil(uidState);
    const usr = getRecoil(usernameState);
    const pass = getRecoil(passwordState);
    const token = getRecoil(tokenState);
    if (token) {
        return {
            usr: usr,
            uid: uid,
            bearer: token,
        }
    }
    return getAuthObjWithCredentials(usr, pass);
}

export function getAuthObjWithCredentials(usr: string, pass: string) {
    const nonce = Date.now();
    const hash = Md5.hashStr(nonce + pass);
    return { usr, pwd: hash, tok: nonce };
}

export function prepareRequest(dataObj = {}) {
    return { auth: getAuthObj(), data: dataObj, target: "base" };
}

export const fetchFromServer = async (endpoint: Endpoint, dataObj: object) => {
    const url = API_ENDPOINT + '/' + endpoint;
    try {
        const result = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(prepareRequest(dataObj)),
        });
        if (result.ok) {
            const json = await result.json()
            if (json.status === STATUS_OK) {
                return { status: true, data: json };
            } else {
                return { status: false, data: json, error: json.status };
            }
        } else {
            return { status: false, data: {}, error: result.status };
        }
    } catch (exc) {
        console.error("fetchFromServer", endpoint, dataObj, exc);
        return { status: false, data: {}, error: STATUS_ERROR_NETWORK };
    }
}

export const getAccounts = async (uids: string[]) => {
    const response = await fetchFromServer(Endpoint.getaccounts, { uids });
    const result = {
        status: response.status,
        accounts: response.data.accounts as Member[],
    };
    return result;
}

export const getAccountUids = async () => {
    const response = await fetchFromServer(Endpoint.getaccountuids, {});
    const result = {
        status: response.status,
        uids: response.data.uids as string[],
    };
    return result;
}

export interface AccountResponse {
    status: boolean;
    account?: Member | Company;
    errorCode?: number;
    /** why the server turned the username down ("length", "chars", …), next to errorMsg */
    invalidUsername?: string;
    errorMessage?: string;
}

export const createAccount = async (data: {}): Promise<AccountResponse> => {
    const response = await fetchFromServer(Endpoint.createaccount, data);
    return {
        status: response.status,
        account: response.data ? response.data.account : undefined,
        errorCode: response.error ? response.error : undefined,
        invalidUsername: typeof response.data?.invalidUsername === "string" ? response.data.invalidUsername : undefined,
        errorMessage: typeof response.data?.errorMsg === "string" ? response.data.errorMsg : undefined,
    }
}

export const editAccount = async (data: {}): Promise<AccountResponse> => {
    const response = await fetchFromServer(Endpoint.editaccount, data);
    if (response.status && "skills" in data) {
        markSkillListStale();
    }
    return {
        status: response.status,
        account: response.data.account
    }
}

export type ChangeUsernameRequest = {
    uid: Uid;
    username: string;
};

export type ChangeUsernameResponse = {
    status: boolean;
    account?: Member;
    username?: string;
    previousUsername?: string;
    errorCode?: number;
    errorMessage?: string;
    invalidUsername?: string;
};

export const changeUsername = async (request: ChangeUsernameRequest): Promise<ChangeUsernameResponse> => {
    const response = await fetchFromServer(Endpoint.changeusername, request);
    return {
        status: response.status,
        account: response.data.account as Member | undefined,
        username: response.data.username,
        previousUsername: response.data.previousUsername,
        errorCode: response.error,
        errorMessage: response.data.errorMsg,
        invalidUsername: typeof response.data.invalidUsername === "string" ? response.data.invalidUsername : undefined,
    }
}

export type RecapConfig = {
    enabled: boolean;
    hour: number;
    memberUids: Uid[];
    notifyEmails: string[];
    pushToMember: boolean;
};

export const getRecapConfig = async (): Promise<{ status: boolean; config?: RecapConfig }> => {
    const response = await fetchFromServer(Endpoint.getrecapconfig, {});
    return { status: response.status, config: response.status ? (response.data as RecapConfig) : undefined };
};

export const saveRecapConfig = async (
    config: RecapConfig,
): Promise<{ status: boolean; backfillStarted?: number }> => {
    const response = await fetchFromServer(Endpoint.saverecapconfig, config);
    return {
        status: response.status,
        // newly enabled members get yesterday rendered immediately — this is
        // how many renders the server just kicked off
        backfillStarted: response.status ? (response.data.backfillStarted as number) || 0 : undefined,
    };
};

export type FlyoverClipStatus = "pending" | "rendering" | "ready" | "failed";

export type FlyoverClipScope =
    | { type: "day"; day: Day }
    | { type: "trip"; day?: Day; trackIds: Uid[] }
    | { type: "range"; day?: Day; from: Timestamp; until: Timestamp }
    | { type: "stop_context"; day?: Day; from: Timestamp; until: Timestamp };

export type FlyoverClip = {
    id: string;
    companyUid?: Uid;
    memberUid: Uid;
    memberName?: string;
    creatorUid?: Uid;
    creatorName?: string;
    type: FlyoverClipScope["type"] | "recap";
    title: string;
    scopeLabel: string;
    status: FlyoverClipStatus;
    day?: Day;
    from: Timestamp;
    until: Timestamp;
    createdAt: Timestamp;
    completedAt?: Timestamp;
    renderStartedAt?: Timestamp;
    failedAt?: Timestamp;
    distanceMeters?: number;
    pointCount?: number;
    stopCount?: number;
    jobId?: string;
    videoUrl?: string;
    downloadUrl?: string;
    streamUid?: string;
    shareToken?: string;
    error?: string;
    /** video length/compression profile chosen at creation */
    pacing?: FlyoverPacing;
    /** simplified [lng,lat] route polyline (≤100 pts) for the card thumbnail */
    previewPath?: [number, number][];
    /** render percent 0-99 while status is rendering */
    renderProgress?: number;
    /** how the video was produced (worker-reported); shown in test env only */
    renderStats?: {
        capture?: string;
        fps?: number;
        width?: number;
        height?: number;
        frames?: number;
        wallSeconds?: number;
        bytes?: number;
        settleMs?: number;
        version?: string;
    };
};

export type FlyoverPacing = "short" | "standard" | "extended";

export const createFlyoverClip = async (
    memberUid: Uid,
    scope: FlyoverClipScope,
    pacing: FlyoverPacing = "standard",
    // Data-first by default: an instant replay saves the scope only and is
    // playable right away; pass true to also queue a video render.
    video = false,
): Promise<{ status: boolean; clip?: FlyoverClip; existing?: boolean; errorCode?: number }> => {
    const response = await fetchFromServer(Endpoint.createflyoverclip, { memberUid, scope, pacing, video });
    return {
        status: response.status,
        clip: response.status ? response.data.clip as FlyoverClip : undefined,
        // server found a clip for the same scope and returned it instead of
        // duplicating (upgrading it in place when a video was requested)
        existing: response.status ? response.data.existing === true : undefined,
        errorCode: response.error,
    };
};

/** Renders a video for an existing replay (also the failed-render retry path). */
export const renderFlyoverClip = async (
    clipId: string,
    pacing?: FlyoverPacing,
): Promise<{ status: boolean; clip?: FlyoverClip; errorCode?: number }> => {
    const response = await fetchFromServer(Endpoint.renderflyoverclip, { clipId, pacing: pacing || "" });
    return {
        status: response.status,
        clip: response.status ? response.data.clip as FlyoverClip : undefined,
        errorCode: response.error,
    };
};

/** Days (YYYYMMDD) within the window that have track data for the member. */
export const getFlyoverDays = async (
    memberUid: Uid,
    from?: Timestamp,
    until?: Timestamp,
): Promise<{ status: boolean; days: Day[] }> => {
    const response = await fetchFromServer(Endpoint.flyoverdays, { memberUid, from: from || 0, until: until || 0 });
    return {
        status: response.status,
        days: response.status ? response.data.days as Day[] : [],
    };
};

export const listFlyoverClips = async (
    filter: { memberUid?: Uid; day?: Day; status?: FlyoverClipStatus; limit?: number } = {},
): Promise<{ status: boolean; clips: FlyoverClip[]; errorCode?: number }> => {
    const response = await fetchFromServer(Endpoint.listflyoverclips, {
        memberUid: filter.memberUid || "",
        day: filter.day || 0,
        status: filter.status || "",
        limit: filter.limit || 100,
    });
    return {
        status: response.status,
        clips: response.status ? response.data.clips as FlyoverClip[] : [],
        errorCode: response.error,
    };
};

export const editFlyoverClip = async (
    clipId: string,
    title: string,
): Promise<{ status: boolean; clip?: FlyoverClip }> => {
    const response = await fetchFromServer(Endpoint.editflyoverclip, { clipId, title });
    return {
        status: response.status,
        clip: response.status ? response.data.clip as FlyoverClip : undefined,
    };
};

export const deleteFlyoverClip = async (clipId: string): Promise<{ status: boolean }> => {
    const response = await fetchFromServer(Endpoint.deleteflyoverclip, { clipId });
    return { status: response.status };
};

export const shareFlyoverClip = async (
    clipId: string,
    revoke = false,
): Promise<{ status: boolean; shareUrl?: string; shareToken?: string }> => {
    const response = await fetchFromServer(Endpoint.shareflyoverclip, { clipId, revoke });
    return {
        status: response.status,
        shareUrl: response.status ? response.data.shareUrl as string : undefined,
        shareToken: response.status ? response.data.shareToken as string : undefined,
    };
};

export type FlyoverEventDto = {
    ts: Timestamp;
    type: string;
    lat?: number;
    lng?: number;
    label?: string;
    /** job number/letter drawn inside the pin (JobMarker-style); jobs only */
    code?: string;
};

export type FlyoverJobMarkerDto = {
    lat: number;
    lng: number;
    code?: string;
    color?: string;
    title?: string;
};

export const getFlyoverEvents = async (
    memberUid: Uid,
    day: Day,
    from: Timestamp,
    until: Timestamp,
): Promise<{ status: boolean; events: FlyoverEventDto[]; jobs: FlyoverJobMarkerDto[] }> => {
    const response = await fetchFromServer(Endpoint.flyoverevents, { memberUid, day, from, until });
    return {
        status: response.status,
        events: response.status ? response.data.events as FlyoverEventDto[] : [],
        jobs: response.status ? (response.data.jobs as FlyoverJobMarkerDto[]) || [] : [],
    };
};

export const deleteAccount = async (uid: Uid): Promise<{ status: boolean }> => {
    const response = await fetchFromServer(Endpoint.deleteaccount, { uid: uid });
    return {
        status: response.status,
    }
}

export interface DeleteTeamResponse {
    status: boolean;
    moved?: { members: number; places: number; zones: number; alerts: number; jobs: number };
}

/** Deletes a team; its members, places, zones, alerts and open jobs move to `moveTo`, or lose the team. */
export const deleteTeam = async (team: number, moveTo?: number): Promise<DeleteTeamResponse> => {
    const response = await fetchFromServer(Endpoint.deleteteam, { team, ...(moveTo ? { moveTo } : {}) });
    return {
        status: response.status,
        moved: response.status ? response.data.moved : undefined,
    };
}

export const deletePlaces = async (uids: Uid[]): Promise<{ status: boolean }> => {
    const response = await fetchFromServer(Endpoint.deleteplaces, { uids: uids });
    return {
        status: response.status,
    }
}

export const getJobById = async (id: string ) => {
    const response = await fetchFromServer(Endpoint.getjobs, { id });
    const result = {
        status: response.status,
        jobs: response.data.jobs as Job[],
    };
    return result;
};

export interface GetJobsRequest {
    day: Day;
    worker: string;
}

export const WORKER_ASSIGNED = "+";
export const WORKER_ALL = "*";

export const getJobs = async ({ day, worker }: GetJobsRequest) => {
    const response = await fetchFromServer(Endpoint.getjobs, { day: day, worker: worker, omitSignatureSvg: true });
    const result = {
        status: response.status,
        jobs: response.data.jobs as Job[],
    };
    return result;
};

export interface AllJobsQuery {
    text?: string;
    allDates?: boolean;
    filterIds?: string[];
    /** the Jobs bar's status, assignee, priority, label and team picks; an older server ignores them */
    jobFilters?: Record<string, string[]>;
    /** ask for the number of jobs per status over the whole range */
    statusCounts?: boolean;
}

export interface JobsVisibilityRequest {
    teamIds: number[];
    includeUnsetTeam: boolean;
}

export const getAllJobs = async (
    config: SortConfig,
    worker: string,
    query?: AllJobsQuery,
    visibility?: JobsVisibilityRequest,
) => {
    const payload: any = {
        worker,
        sort: config,
        omitSignatureSvg: true,
    };
    if (query) {
        payload.query = query;
    }
    if (visibility && visibility.teamIds.length > 0) {
        payload.teamIds = visibility.teamIds;
        payload.includeUnsetTeam = visibility.includeUnsetTeam;
    }
    const response = await fetchFromServer(Endpoint.getjobs,
        payload);
    const result = {
        status: response.status,
        jobs: response.data.jobs as Job[],
        totalCount: response.data.totalCount as number | undefined,
        hasMore: response.data.hasMore as boolean | undefined,
        statusCounts: response.data.statusCounts as Record<string, number> | undefined,
    };
    return result;
};

export const getAllUnassignedJobs = async (visibility?: JobsVisibilityRequest) => {
    const payload: any = {
        worker: "",
        day: 0,
        sort: {
            field: "",
            ascending: false,
            limit: 500,
            skip: 0,
            from: 0,
            until: Date.now(),
            status: 0,
        } as SortConfig,
        omitSignatureSvg: true,
    };
    if (visibility && visibility.teamIds.length > 0) {
        payload.teamIds = visibility.teamIds;
        payload.includeUnsetTeam = visibility.includeUnsetTeam;
    }
    const response = await fetchFromServer(Endpoint.getjobs, payload);
    const result = {
        status: response.status,
        jobs: response.data.jobs as Job[],
    };
    return result;
};

export interface JobsResponse {
    status: boolean;
    jobs: Job[];
}

export interface JobGroupsResponse {
    status: boolean;
    jobGroups: JobGroup[];
}

export const editJobs = async (data: {}): Promise<JobsResponse> => {
    const response = await fetchFromServer(Endpoint.editjobs, {
        jobs: data,
        notify: true,
        sendRouteChange: true,
    });
    if (response.status && Object.values(data).some(job => job && typeof job === "object" && "requiredSkills" in job)) {
        markSkillListStale();
    }
    return {
        status: response.status,
        jobs: response.data.jobs || []
    };
}

export const createJobs = async (data: {}): Promise<JobsResponse> => {
    const response = await fetchFromServer(Endpoint.createjobs, {
        jobs: data,
        sendRouteChange: true,
    });
    return {
        status: response.status,
        jobs: response.data.jobs || []
    }
}

export const getJobGroups = async ({
    ids,
    query,
    includeJobs = false,
}: {
    ids?: string[];
    query?: string;
    includeJobs?: boolean;
}): Promise<JobGroupsResponse> => {
    const response = await fetchFromServer(Endpoint.getjobgroups, {
        ids,
        query,
        includeJobs,
    });
    return {
        status: response.status,
        jobGroups: response.data.jobGroups || [],
    };
}

export const createJobGroups = async (jobGroups: Partial<JobGroup>[]): Promise<JobGroupsResponse> => {
    const response = await fetchFromServer(Endpoint.createjobgroups, {
        jobGroups,
    });
    return {
        status: response.status,
        jobGroups: response.data.jobGroups || [],
    };
}

export const editJobGroups = async (jobGroups: Record<string, Partial<JobGroup>>): Promise<JobGroupsResponse> => {
    const response = await fetchFromServer(Endpoint.editjobgroups, {
        jobGroups,
    });
    return {
        status: response.status,
        jobGroups: response.data.jobGroups || [],
    };
}

export const deleteJobGroups = async (ids: string[]): Promise<{ status: boolean; ids: string[]; jobs: Job[] }> => {
    const response = await fetchFromServer(Endpoint.deletejobgroups, { ids });
    return {
        status: response.status,
        ids: response.data.ids || [],
        jobs: response.data.jobs || [],
    };
}

export const archiveJobs = async (data: {}): Promise<JobsResponse> => {
    const response = await fetchFromServer(Endpoint.archivejobs, {
        jobs: data
    });
    return {
        status: response.status,
        jobs: response.data.jobs,
    }
}

export const restoreJobs = async (data: {}) => {
    const response = await fetchFromServer(Endpoint.restorejobs, {
        jobs: data
    });
    return {
        status: response.status,
        jobs: response.data.jobs,
    }
}

export interface CloneJobsRequest {
    withAssignee: boolean;
    withDate: boolean;
    day?: number;
    assigneeUid?: string; // replaces the copied assignee when withAssignee is set
    suffix: string;
    jobs: string[]; // job ids
    templates: string[]; // template ids
}

export const cloneJobs = async (req: CloneJobsRequest): Promise<JobsResponse> => {
    const response = await fetchFromServer(Endpoint.clonejobs, req);
    return {
        status: response.status,
        jobs: response.data.jobs || []
    }
}

/** A company's saved way of reading one kind of import file; the content is the dashboard's own. */
export interface ImportLayoutRecord {
    id: string;
    type: "jobs" | "places" | "zones" | "members" | "sequences";
    name: string;
    updatedTs?: number;
    updatedBy?: string;
    [key: string]: unknown;
}

export const getImportLayouts = async (): Promise<{ status: boolean; layouts: ImportLayoutRecord[] }> => {
    const response = await fetchFromServer(Endpoint.importlayouts, { action: "get" });
    return { status: response.status, layouts: response.status ? response.data.layouts ?? [] : [] };
}

export const saveImportLayout = async (layout: ImportLayoutRecord): Promise<{ status: boolean; layouts: ImportLayoutRecord[] }> => {
    const response = await fetchFromServer(Endpoint.importlayouts, { action: "save", layout });
    return { status: response.status, layouts: response.status ? response.data.layouts ?? [] : [] };
}

export const deleteImportLayout = async (id: string): Promise<{ status: boolean; layouts: ImportLayoutRecord[] }> => {
    const response = await fetchFromServer(Endpoint.importlayouts, { action: "delete", id });
    return { status: response.status, layouts: response.status ? response.data.layouts ?? [] : [] };
}

export const deleteJobs = async (data: {}): Promise<JobsResponse> => {
    const response = await fetchFromServer(Endpoint.deletejobs, {
        jobs: data,
        notify: true,
    });
    return {
        status: response.status,
        jobs: response.data.jobs,
    };
}

export const saveZone = async (zone: Zone) => {
    const response = await fetchFromServer(Endpoint.savezone, {
        zone: zone,
    });
    return {
        status: response.status,
        zone: response.data.zone,
    };
}

export const deleteZone = async (zone: Zone) => {
    const response = await fetchFromServer(Endpoint.deletezone, { uid: zone.uid });
    return {
        status: response.status,
    };
}

export const loadSequences = async () => {
    // history: recent per-route assignment/completion summaries (30 days back),
    // shown in the "Load route" picker and on planner cells.
    // futureDays: the coming days that already have jobs too, for the route card's Upcoming (older runtimes ignore it)
    const response = await fetchFromServer(Endpoint.loadsequences, { archived: false, history: true, historyDays: 30, futureDays: 31 });
    return {
        status: response.status,
        sequences: response.data.sequences as ReadableSequence[],
    };
}

export const editSequenceJob = async (sequenceId: string, jobId: string, data: {}) => {
    const response = await fetchFromServer(Endpoint.editsequencejob, { ...data, sequenceId: sequenceId, jobId: jobId });
    return {
        status: response.status,
        sequence: response.data?.sequence || {} as ReadableSequence,
    };
}

export const saveSequence = async (sequence: SavableSequence) => {
    const response = await fetchFromServer(Endpoint.savesequence, { sequence: sequence });
    return {
        status: response.status,
        sequence: response.data.sequence as ReadableSequence,
    };
}

export const assignSequence = async (sequenceId: string, assigneeUid: Uid, day = dayOfToday()) => {
    const response = await fetchFromServer(Endpoint.assignsequence, {
        id: sequenceId,
        assignee: assigneeUid,
        day,
    });
    return {
        status: response.status,
        jobs: response.data.jobs as Job[],
    };
}

/**
 * Brings the jobs a route already made in line with the route as it is now. With dryRun (the default) nothing
 * changes and the answer says what would; only jobs nobody started are ever replaced.
 */
// changedOnly: only the days whose person changes or that no longer run (the stops stayed the same)
export const updateSequenceJobs = async (sequenceId: string, { fromDay, untilDay, dryRun = true, changedOnly = false }: { fromDay?: Day; untilDay?: Day; dryRun?: boolean; changedOnly?: boolean }) => {
    const response = await fetchFromServer(Endpoint.updatesequencejobs, { id: sequenceId, fromDay, untilDay, dryRun, changedOnly });
    return {
        status: response.status,
        days: (response.data?.days ?? []) as SequenceDayUpdate[],
        sequence: response.data?.sequence as ReadableSequence | undefined,
    };
}

// Read-only projection of recurring sequences that WOULD materialize in [fromDay, toDay]
// but haven't yet — the source of the week planner's ghost chips.
export const projectSequences = async ({ fromDay, toDay }: { fromDay: Day; toDay: Day }) => {
    const response = await fetchFromServer(Endpoint.projectsequences, { fromDay, toDay });
    return {
        status: response.status,
        occurrences: (response.data?.occurrences ?? []) as ProjectedOccurrence[],
    };
}

// Materialize one projected occurrence into real jobs (idempotent server-side).
export const materializeSequence = async (sequenceId: string, assigneeUid: Uid, day: Day) => {
    const response = await fetchFromServer(Endpoint.materializesequence, {
        id: sequenceId,
        assignee: assigneeUid,
        day,
    });
    return {
        status: response.status,
        jobs: (response.data?.jobs ?? []) as Job[],
    };
}

// Preview an optimized whole-week plan (read-only, server persists nothing). Days
// are the visible board columns; the loop spills carry-over jobs that don't fit a
// day into the next. The dispatcher confirms via confirmWeekPlan.
// `workers` scopes the solve to the board's roster — their jobs plus unassigned
// ones. Empty plans the whole company.
// `days` is the exact set of days to solve — days left out are never touched and
// nothing spills into them. `workers` scopes the solve to the board's roster; empty
// plans the whole company. `includeUnassigned` decides whether open jobs may be
// pulled onto that roster.
export const planWeek = async (
    days: Day[],
    workers: Uid[] = [],
    includeUnassigned = true,
    snapshotHash = "",
) => {
    const response = await fetchFromServer(Endpoint.planweek, {
        days,
        workers,
        include_unassigned: includeUnassigned,
        snapshot_hash: snapshotHash,
        freeze_minutes: 45,
    });
    return {
        status: response.status,
        plan: (response.data?.plan ?? undefined) as WeekPlan | undefined,
    };
}

// Apply a confirmed week plan — a list of {jobId, toDay, toWorkerId, position}
// moves — via the same assignment primitive as a single-job drag.
export const confirmWeekPlan = async (moves: WeekPlanApply[]) => {
    const response = await fetchFromServer(Endpoint.confirmweekplan, {
        moves: moves.map((move) => ({
            jobId: move.jobId,
            toDay: move.toDay,
            toWorkerUid: move.toWorkerId,
            position: move.position,
        })),
    });
    return {
        status: response.status,
        jobs: (response.data?.jobs ?? []) as Job[],
    };
}

export const getRecentJobPatterns = async (params: { days?: number, limit?: number } = {}) => {
    const response = await fetchFromServer(Endpoint.getrecentjobpatterns, params);
    return {
        status: response.status,
        patterns: (response.data?.patterns ?? []) as JobPattern[],
    };
}

export const getLocationInsights = async (params: { placeUid?: string, lat?: number, lng?: number, day: number }) => {
    const response = await fetchFromServer(Endpoint.getlocationinsights, params);
    return {
        status: response.status,
        insights: response.status ? response.data as LocationInsights : undefined,
    };
}

/** Spots the company keeps sending jobs to without a place, busiest first. */
export const getPlaceSuggestions = async (params: { days?: number, limit?: number } = {}) => {
    const response = await fetchFromServer(Endpoint.getplacesuggestions, params);
    return {
        status: response.status,
        suggestions: (response.data?.suggestions ?? []) as PlaceSuggestion[],
    };
}

/** Named areas with a real border (neighbourhoods, districts, suburbs, postcodes) for a new zone, nearest to `near` first. */
export const areaOutlines = async (q: string, near?: { lat: number, lng: number }, typing = false) => {
    // typing asks only our own neighbourhoods, which is quick and allowed on every keystroke
    const response = await fetchFromServer(Endpoint.areaoutlines, { q, ...near, ...(typing && { typing }) });
    return {
        status: response.status,
        areas: (response.data?.areas ?? []) as AreaOutline[],
    };
}

export const suggestAssignees = async (params: { lat: number, lng: number, day: number, limit?: number }) => {
    const response = await fetchFromServer(Endpoint.suggestassignees, params);
    return {
        status: response.status,
        suggestions: (response.data?.suggestions ?? []) as AssigneeSuggestion[],
    };
}

// stopsOnly + startSec: a recurring route's planned day, its stops alone from its own start time
export const draftRoute = async (params: { account: Uid, day: number, stops: DraftRouteStop[], optimize?: boolean, stopsOnly?: boolean, startSec?: number }) => {
    const response = await fetchFromServer(Endpoint.draftroute, params);
    return {
        status: response.status,
        plan: response.status ? response.data as DraftRoutePlan : undefined,
    };
}

/** The order a person's day had before a better order, kept on the server so Undo works anywhere. */
export const routeUndo = async (params: { account: Uid, day: number, action: "get" | "save" | "clear", order?: string[], after?: string[] }) => {
    const response = await fetchFromServer(Endpoint.routeundo, params);
    return {
        status: response.status,
        order: (response.data?.order ?? []) as string[],
        after: (response.data?.after ?? []) as string[],
    };
}

export const getJobTemplates = async () => {
    const response = await fetchFromServer(Endpoint.getjobtemplates, {});
    return {
        status: response.status,
        jobs: response.data.jobs as Job[],
    }
}

export interface LoadFormSubmissionsRequest {
    from: number;
    until: number;
    limit: number;
}

export interface LoadFormSubmissionIdsRequest {
    submissionIds?: string[];
}

export const loadFormSubmissions = async (req: LoadFormSubmissionsRequest|LoadFormSubmissionIdsRequest) => {
    const response = await fetchFromServer(Endpoint.loadformsubmissions, req);
    return {
        status: response.status,
        formsubmissions: response.data.formsubmissions as FormTemplate[],
    }
}

export interface LoadVoizReportsRequest {
    from?: number;
    until?: number;
    limit?: number;
    offset?: number;
    search?: string;
    jobId?: string;
    placeUid?: string;
}

export const loadVoizReports = async (req: LoadVoizReportsRequest = {}) => {
    const response = await fetchFromServer(Endpoint.loadvoizreports, req);
    return {
        status: response.status,
        voizreports: response.data.voizreports as VoizReportSubmission[],
        total: response.data.total as number,
        hasMore: response.data.hasMore as boolean,
        offset: response.data.offset as number,
        limit: response.data.limit as number,
    };
}

export const loadPublicFormSubmissions = async (req: LoadFormSubmissionsRequest|LoadFormSubmissionIdsRequest) => {
    const response = await fetchFromServer(Endpoint.loadpublicformsubmissions, req);
    return {
        status: response.status,
        formsubmissions: response.data.formsubmissions as FormTemplate[],
    }
}

export const loadFormTemplates = async () => {
    const response = await fetchFromServer(Endpoint.loadformtemplates, {});
    return {
        status: response.status,
        formtemplates: response.data.formtemplates as FormTemplate[],
    }
}

export const saveFormTemplate = async (form: FormTemplate) => {
    const data = { formContent: JSON.stringify(form.content) } as any;
    if (form.id) {
        data.templateId = form.id;
    }
    const response = await fetchFromServer(Endpoint.saveformtemplate, data);
    return {
        status: response.status,
        id: response.data.id as string,
        formTemplates: response.data.formtemplates as FormTemplate[],
    }
}

export const deleteFormTemplate = async (form: FormTemplate) => {
    const response = await fetchFromServer(Endpoint.saveformtemplate, {
        templateId: form.id,
        formContent: JSON.stringify(form.content),
        status: 2
    });
    return {
        status: response.status,
        id: response.data.id as string,
        formTemplates: response.data.formtemplates as FormTemplate[],
    }
}

export const saveJobAsTemplate = async (req: { id: string; }) => {
    const response = await fetchFromServer(Endpoint.savejobastemplate, req);
    return {
        status: response.status,
        // the runtime answers with the new template, not a bare id
        id: (response.data?.template?.id ?? response.data?.id) as string | undefined,
    }
}

export const getAllConversations = async () => {
    const response = await fetchFromServer(Endpoint.conversations, {});
    return {
        status: response.status,
        messages: response.data.messages as Message[],
    }
}

export interface ConversationRequest {
    account: string;
    /** false loads without marking the person's messages read; servers before Oct 2026 ignore it and mark them */
    markRead?: boolean;
}

export const getConversation = async (req: ConversationRequest) => {
    const response = await fetchFromServer(Endpoint.conversation, req);
    return {
        status: response.status,
        messages: response.data.messages as Message[],
    }
}

export interface SendMessageRequest {
    msg: string;
    receivers?: string[],
    lat?: number;
    lng?: number;
}

export const sendMessage = async (req: SendMessageRequest) => {
    const response = await fetchFromServer(Endpoint.sendmsg, req);
    return { status: response.status }
}

export interface DeleteMessageRequest {
    ids: string[]
}

export const deleteMessage = async (req: DeleteMessageRequest) => {
    const response = await fetchFromServer(Endpoint.delmsg, req);
    return { status: response.status }
}

export const getAlertInstances = async () => {
    const response = await fetchFromServer(Endpoint.getalertinstances, {});
    return {
        status: response.status,
        alertinstances: response.data.alertinstances as AlertInstance[],
    }
}

export const getAlertConfigs = async () => {
    const response = await fetchFromServer(Endpoint.getalertconfigs, {});
    return {
        status: response.status,
        alertconfigs: response.data.alertconfigs as AlertConfig[],
    }
}

export const saveAlertConfig = async (alertConfig: AlertConfig) => {
    const response = await fetchFromServer(Endpoint.savealertconfig, {
        alertconfig: alertConfig
    });
    return {
        status: response.status,
        alertconfigs: response.data.alertconfigs as AlertConfig[],
    }
}

export const getTimesheet = async (day: Day) => {
    const response = await fetchFromServer(Endpoint.gettimesheet, { day: day });
    return {
        status: response.status,
        timesheet: response.data.timesheet as TimesheetEntry[],
    }
}

export const sendResetPasswordLinkTo = async (uidOrUsername: string) => {
    const response = await fetchFromServer(Endpoint.requestpassword, { account: uidOrUsername });
    return {
        status: response.status,
        error: response.error,
        email: response.data.email,
        phone: response.data.phone,
    }
}

export const startVerifyEmail = async (uid: Uid) => {
    const response = await fetchFromServer(Endpoint.startverifyemail, { uid: uid });
    return {
        status: response.status,
        email: response.data.email,
    }
}

export const verifyEmailCode = async (code: string) => {
    const response = await fetchFromServer(Endpoint.verifyemailcode, { code });
    return {
        status: response.status,
        verified: !!response.data.verified,
        attemptsLeft: response.data.attemptsLeft as number | undefined,
        error: response.error,
    }
}

export const startVerifyPhone = async (uid: Uid) => {
    const response = await fetchFromServer(Endpoint.startverifyphone, { uid: uid });
    return {
        status: response.status,
        code: response.data.code,
        phone: response.data.phone,
    }
}

export const editInviteCode = async (data: { inviteCode: string, inviteRemainingSeconds: number }) => {
    const response = await fetchFromServer(Endpoint.editinvitecode, data);
    return {
        status: response.status,
    }
}

export const createInviteCode = async ({
                                           companyUid,
                                           inviteRemainingSeconds
                                       }: { companyUid: Uid, inviteRemainingSeconds: number }) => {
    const response = await fetchFromServer(Endpoint.createinvitecode, {
        inviteRemainingSeconds,
        groupUid: companyUid,
    });
    return {
        status: response.status,
    }
}

export const resetGoogleCalendar = async ({ uid }: { uid: Uid }) => {
    const response = await fetchFromServer(Endpoint.resetfield, { uid: uid, field: "google_calendar" });
    return {
        status: response.status,
    }
}

export const sendGoogleCalendarInvite = async ({ uid }: { uid: Uid }) => {
    const response = await fetchFromServer(Endpoint.sendgooglecalendarinvite, { uid: uid });
    return {
        status: response.status,
        receiver: response.data.receiver as string,
    }
}

export const disconnectSlack = async () => {
    const response = await fetchFromServer(Endpoint.disconnectslack, {});
    return { status: response.status }
}

export const createPlace = async (data: {}) => {
    const response = await fetchFromServer(Endpoint.createplace, data);
    return {
        status: response.status,
        uid: response.data?.uid as Uid | undefined,
    };
}

export const editPlace = async (data: {}) => {
    const response = await fetchFromServer(Endpoint.editplace, {
        ...data,
    });
    return {
        status: response.status
    };
}

export const applyPlacesToPastVisits = async () => {
    const response = await fetchFromServer(Endpoint.applyplacestopastvisits, {});
    return {
        status: response.status
    };
}

export const loadUploadedReports = async () => {
    const response = await fetchFromServer(Endpoint.loaduploadedreports, {});
    return {
        status: response.status,
        reports: response.data.reports as UploadedReport[],
    };
}

export const loadReportJob = async (jobId: string) => {
    const response = await fetchFromServer(Endpoint.loadreportjob, { jobId });
    return {
        status: response.status,
        job: response.data.job as ReportJob,
    };
}

export const loadReportJobs = async () => {
    const response = await fetchFromServer(Endpoint.loadreportjobs, {});
    return {
        status: response.status,
        jobs: response.data.jobs as ReportJob[],
    };
}

export const loadRecurringReportConfigs = async () => {
    const response = await fetchFromServer(Endpoint.loadrecurringreportconfigs, {});
    return {
        status: response.status,
        configs: response.data.configs as RecurringReportConfig[],
    };
}

export const saveRecurringReportConfig = async (config: RecurringReportConfig) => {
    const response = await fetchFromServer(Endpoint.saverecurringreportconfig, { config });
    return {
        status: response.status,
        configs: response.data.configs as RecurringReportConfig[],
    };
}

interface CreateCompanyRequest {
    name: string;
    formattedAddress: string;
    address: string;
    city: string;
    countryCode: string;
    streetName: string;
    streetNumber: string;
    postalCode: string;
    lat: Latitude;
    lng: Longitude;
    users?: string;
    goal?: string;
}

export const createCompany = async (data: CreateCompanyRequest) => {
    const response = await fetchFromServer(Endpoint.createcompany, data);
    return {
        status: response.status,
        company: response.data.company as Company,
    };
}

export const setProfileImageInitials = async (uid: Uid, initials: string) => {
    const response = await fetchFromServer(Endpoint.setprofileimageinitials, { uid, initials });
    return {
        status: response.status,
    };
}

export const startRouteSimulation = async (uidDay: UidDay) => {
    const response = await fetchFromServer(Endpoint.startroutesimulation, uidDay);
    return {
        status: response.status,
    };
}

export const stopRouteSimulation = async (uidDay: UidDay) => {
    const response = await fetchFromServer(Endpoint.stoproutesimulation, uidDay);
    return {
        status: response.status,
    };
}

export interface SaveTimeSheetEntryRequest {
    memberUid: Uid;
    clockInId: string;
    clockOutId: string;
    clockInTs: Timestamp;
    clockOutTs: Timestamp;
}

export const saveTimeSheetEntry = async (request: SaveTimeSheetEntryRequest) => {
    const response = await fetchFromServer(Endpoint.savetimesheetentry, request);
    return {
        status: response.status,
    };
}

export const deleteTracks = async (memberUid: Uid, entries: TimesheetEntry[]) => {
    const response = await fetchFromServer(Endpoint.deletetracks, { memberUid, trackIds: entries.map(e => e.uid) });
    return {
        status: response.status,
    };
}

export const publishTracks = async (memberUid: Uid, entries: TimesheetEntry[], publish: boolean) => {
    const response = await fetchFromServer(Endpoint.publishtracks, {
        memberUid,
        publish,
        trackIds: entries.map(e => e.uid)
    });
    return {
        status: response.status,
    };
}

export const editTrackApprovalStatus = async (trackEntry: TrackEntry, approvalStatus: ApprovalStatus, rejectionNote?: string) => {
    const response = await fetchFromServer(Endpoint.edittrack, {
        track: trackEntry.uid,
        approval_status: approvalStatus,
        ...(rejectionNote !== undefined ? { rejection_note: rejectionNote } : {}),
    });
    return {
        status: response.status,
    };
};

/**
 * Structural trip fields shared by /createtrack and /edittrack. Timestamps in epoch ms.
 * The web dashboard never sends a distance: the server derives it from the route
 * (straight line when no route is found).
 */
export interface TrackDetails {
    startTs: Timestamp;
    endTs: Timestamp;
    distance?: number;
    startLat: Latitude;
    startLng: Longitude;
    startText?: string;
    startPlace?: Uid;
    endLat: Latitude;
    endLng: Longitude;
    endText?: string;
    endPlace?: Uid;
    purpose?: string;
    comment?: string;
    travelMode?: string;
}

export interface CreateTrackRequest extends TrackDetails {
    /** operators/admins creating for a member; omit for self */
    memberUid?: Uid;
}

export interface TrackMutationResult {
    status: boolean;
    track?: TrackEntry;
    /** server-side validation message (status -11), meant to be shown verbatim */
    errorMsg?: string;
    errorCode?: number;
}

function trackMutationResult(response: Awaited<ReturnType<typeof fetchFromServer>>): TrackMutationResult {
    return {
        status: response.status,
        track: response.data?.track as TrackEntry | undefined,
        errorMsg: typeof response.data?.errorMsg === "string" ? response.data.errorMsg : undefined,
        errorCode: response.status ? undefined : response.error,
    };
}

export const createTrack = async (request: CreateTrackRequest): Promise<TrackMutationResult> => {
    const response = await fetchFromServer(Endpoint.createtrack, request);
    return trackMutationResult(response);
};

export const editTrackDetails = async (trackId: Uid, details: Partial<TrackDetails>): Promise<TrackMutationResult> => {
    const response = await fetchFromServer(Endpoint.edittrack, { track: trackId, ...details });
    return trackMutationResult(response);
};

interface PasswordRecoveryRequest {
    token: string;
    newPassword?: string;
    newName?: string;
}

interface PasswordRecoveryResponse {
    status: boolean;
    name?: string;
    username?: string;
    redirect: "login" | "getapp";
    invalidPasswordMessage?: string | undefined;
    errorCode?: number;
}

export const passwordRecovery = async (request: PasswordRecoveryRequest): Promise<PasswordRecoveryResponse> => {
    const response = await fetchFromServer(Endpoint.passwordrecovery, request);
    return {
        status: response.status,
        name: response.data?.name,
        username: response.data?.username,
        redirect: response.data?.redirect,
        invalidPasswordMessage: response.data?.invalidPasswordMessage,
        errorCode: response.error,
    };
}

interface ActivationRequest {
    token: string;
}

interface ActivationResponse {
    status: boolean;
    recoveryToken?: string;
    redirect?: 'passwordrecovery' | 'login' | 'getapp';
    errorCode?: number;
}

export const activation = async (request: ActivationRequest): Promise<ActivationResponse> => {
    const response = await fetchFromServer(Endpoint.activation, request);
    return {
        status: response.status,
        errorCode: response.error,
        recoveryToken: response.data.recoveryToken ? response.data.recoveryToken : undefined,
        redirect: response.data.redirect ? response.data.redirect : undefined,
    };
}

export type StatsRequestOptions = {
    memberUids?: Uid[];
    teamIds?: number[];
    compare?: boolean;
    /** places whose clocked time counts as "at places" (stats/members, stats/history); undefined = every place */
    placeUids?: Uid[];
    /** the board shows clocked time at places: past days stored without it are filled in (in the background) */
    clockedTime?: boolean;
};

function statsRequestData(fromDay: Day, untilDay: Day, options: StatsRequestOptions) {
    return {
        fromDay,
        untilDay,
        ...(options.memberUids && options.memberUids.length > 0 ? { uids: options.memberUids } : {}),
        ...(options.teamIds && options.teamIds.length > 0 ? { teamIds: options.teamIds } : {}),
        ...(options.compare ? { compare: true } : {}),
        ...(options.placeUids ? { placeUids: options.placeUids } : {}),
        ...(options.clockedTime ? { clockedTime: true } : {}),
    };
}

export const statsMembers = async (fromDay: Day, untilDay: Day, options: StatsRequestOptions = {}) => {
    const response = await fetchFromServer(Endpoint.statsmembers, statsRequestData(fromDay, untilDay, options));
    return {
        status: response.status,
        stats: normalizeMemberStats(response.data.stats),
        prev: response.data.prev ? normalizeMemberStats(response.data.prev) : undefined,
        usage: response.data.usage as UsageSignals | undefined,
        /** combined over all members in the response (exact); undefined with older backends */
        dist: normalizeDistributions(response.data.dist),
        prevDist: normalizeDistributions(response.data.prevDist),
    };
}

export const statsMembersByDay = async (fromDay: Day, untilDay: Day, options: StatsRequestOptions = {}) => {
    const response = await fetchFromServer(Endpoint.statsmembers, {
        ...statsRequestData(fromDay, untilDay, options),
        bucketByDay: true,
    });
    return {
        status: response.status,
        stats: normalizeMemberStatDays(response.data.stats),
        prev: response.data.prev ? normalizeMemberStatDays(response.data.prev) : undefined,
        usage: response.data.usage as UsageSignals | undefined,
        /** past days are still being rolled up; asking again later fills them in */
        partial: response.data.partial === true,
    };
}

/** Per-member per-day series from the daily rollup (today computed live). Max 120 days. */
export const statsHistory = async (fromDay: Day, untilDay: Day, options: StatsRequestOptions = {}) => {
    const response = await fetchFromServer(Endpoint.statshistory, statsRequestData(fromDay, untilDay, options));
    return {
        status: response.status,
        days: normalizeMemberStatDays(response.data.days),
        partial: response.data.partial === true,
    };
}

export const statsAttention = async (fromDay: Day, untilDay: Day, options: StatsRequestOptions & { muted?: AttentionMute[] } = {}) => {
    const response = await fetchFromServer(Endpoint.statsattention, {
        ...statsRequestData(fromDay, untilDay, options),
        ...(options.muted && options.muted.length > 0 ? { muted: options.muted } : {}),
    });
    return {
        status: response.status,
        items: (response.data.items || []) as AttentionItem[],
        baseline: (response.data.baseline || "ready") as "ready" | "building",
    };
}

/** Place dimension: visits per place in the window, coverage and silent places. Undefined `places` = older backend. */
export const statsPlaces = async (fromDay: Day, untilDay: Day, options: StatsRequestOptions = {}) => {
    const response = await fetchFromServer(Endpoint.statsplaces, statsRequestData(fromDay, untilDay, options));
    return {
        status: response.status,
        places: response.status ? normalizePlaceStats(response.data) : undefined,
    };
}

/** Form dimension: submissions per form template in the window. Undefined `forms` = older backend. */
export const statsForms = async (fromDay: Day, untilDay: Day, options: StatsRequestOptions = {}) => {
    const response = await fetchFromServer(Endpoint.statsforms, statsRequestData(fromDay, untilDay, options));
    return {
        status: response.status,
        forms: response.status ? normalizeFormStats(response.data) : undefined,
    };
}

export type VisitRequestOptions = StatsRequestOptions & {
    /** only these job types; empty = all */
    jobTypes?: number[];
    split?: VisitSplit;
};

function visitRequestData(fromDay: Day, untilDay: Day, options: VisitRequestOptions) {
    return {
        ...statsRequestData(fromDay, untilDay, options),
        ...(options.jobTypes && options.jobTypes.length > 0 ? { jobTypes: options.jobTypes } : {}),
        ...(options.split ? { split: options.split } : {}),
    };
}

/** Visit length (check-in to check-out per job) in the window. Undefined `visits` = older backend. */
export const statsVisits = async (fromDay: Day, untilDay: Day, options: VisitRequestOptions = {}) => {
    const response = await fetchFromServer(Endpoint.statsvisits, visitRequestData(fromDay, untilDay, options));
    return {
        status: response.status,
        visits: response.status ? normalizeVisitStats(response.data) : undefined,
    };
}

/** Job ids of the visits whose length lies in [fromMs, untilMs) (untilMs 0 = no upper end), newest first, capped. */
export const statsVisitJobIds = async (fromDay: Day, untilDay: Day, range: { fromMs: number; untilMs: number }, options: VisitRequestOptions = {}) => {
    const response = await fetchFromServer(Endpoint.statsvisits, { ...visitRequestData(fromDay, untilDay, options), range });
    return {
        status: response.status,
        jobIds: (response.status ? response.data.jobIds || [] : []) as string[],
        total: Number(response.data?.total) || 0,
    };
}

/** Everyone's activities between two moments in one call: the people this account may see, plus the company. */
export const getTeamActivities = async (from: Timestamp, until: Timestamp, max: number) => {
    const response = await fetchFromServer(Endpoint.getactivities, { from, until, max });
    return {
        status: response.status,
        activities: (response.status ? response.data.activities || [] : []) as Activity[],
    };
}

export const resendInvite = async (uid: Uid) => {
    const response = await fetchFromServer(Endpoint.resendinvite, { uid });
    return {
        status: response.status
    }
}

interface DistributeJobsRequest {
    regions: number;
    jobs: string[];
    max?: number;
    method: "distance" | "zones";
}

export const distributeJobs = async (req: DistributeJobsRequest) => {
    const mappedIds = req.jobs.reduce((obj: any, id) => {
        obj[id] = {};
        return obj;
    }, {});
    const response = await fetchFromServer(Endpoint.distributejobs, {
        regions: req.regions,
        jobs: mappedIds,
        method: req.method,
        max: req.max
    });
    return {
        status: response.status,
        clusters: response.data.clusters as Cluster[],
    };
}

export const submitForm = async (content: FormContent) => {
    const response = await fetchFromServer(Endpoint.submitform, {
        content: JSON.stringify(content), 
    });

    return {
        status: response.status,
        submissionUid: response.data.submissionUid
    };
}

interface GetChecksRequest {
    uid: Uid;
    fromTs: Timestamp;
    filtering: boolean;
}

export const getChecks = async (req: GetChecksRequest) => {
    const response = await fetchFromServer(Endpoint.getchecks, req);

    return {
        status: response.status,
        checks: response.data.checks as Check[]
    }
}

export const getTrips = async (req: { accountUids: Uid[] }) => {
    const response = await fetchFromServer(Endpoint.gettrips, req);

    return {
        status: response.status,
        trips: response.data.trips as Trip[]
    }
}

export const getMemberLiveBadges = async (req: { uids: Uid[] }) => {
    const response = await fetchFromServer(Endpoint.getmemberlivebadges, req);

    return {
        status: response.status,
        badges: response.data.badges as MemberLiveBadge[]
    }
}

export const getLogHistory = async (req: { memberUid: string, from: number, until: number }) => {
    const response = await fetchFromServer(Endpoint.getloghistory, req);

    return {
        status: response.status,
        logs: response.data.logs as LogEvent[],
    }
}

export const matchAssignees = async ({ jobs, draft, members, allMembers }: {
    jobs: string[], draft?: string, members: Uid[], allMembers?: boolean
}) => {
    const response = await fetchFromServer(Endpoint.matchaibatch, { jobs, draft, members, allMembers });
    return {
        status: response.status,
        jobs: (response.data.jobs || {}) as { [key: string]: JobAssigneeFacts },
    }
}

export const suggestJobSkills = async ({ text, selected, members }: { text: string, selected: string[], members: Uid[] }) => {
    const response = await fetchFromServer(Endpoint.matchaiskills, { text, selected, members });
    return {
        status: response.status,
        skills: (response.data.skills || []) as string[],
        nobodyHas: (response.data.nobodyHas || []) as string[],
    }
}

export const getMemberSkillProposals = async ({ uid, members }: { uid: Uid, members: Uid[] }) => {
    const response = await fetchFromServer(Endpoint.matchaimemberskills, { uid, members });
    return {
        status: response.status,
        proposals: (response.data.proposals || []) as SkillEvidence<{ skill: string }>[],
    }
}

/** Members whose completed jobs point to a skill they do not have on their profile. */
export const getSkillCandidates = async ({ skill, members }: { skill: string, members: Uid[] }) => {
    const response = await fetchFromServer(Endpoint.matchaiskillcandidates, { skill, members });
    return {
        status: response.status,
        candidates: (response.data.candidates || []) as SkillEvidence<{ uid: Uid }>[],
    }
}

/** Renames a skill on members, templates and open jobs; an existing word merges, an empty one removes. */
export const renameSkill = async ({ from, to }: { from: string, to: string }) => {
    const response = await fetchFromServer(Endpoint.matchaiskillrename, { from, to });
    return {
        status: response.status,
        members: (response.data.members || 0) as number,
        templates: (response.data.templates || 0) as number,
        jobs: (response.data.jobs || 0) as number,
    }
}

export const getSkillList = async ({ members, typed, listed, upcomingDays }: { members: Uid[], typed?: string, listed?: string, upcomingDays?: number }) => {
    const response = await fetchFromServer(Endpoint.matchaiskilllist, { members, typed, listed, upcomingDays });
    return {
        status: response.status,
        skills: (response.data.skills || []) as SkillListEntry[],
        twin: (response.data.twin || "") as string,
        upcoming: (response.data.upcoming || {}) as { [skill: string]: number },
    }
}

export const routingMatrix = async ({ jobId, uids, destination }: { jobId?: string, destination?: LatLng, uids: Uid[] }) => {
    const response = await fetchFromServer(Endpoint.routingmatrix, {
        sourceUids: uids,
        destinationJobs: jobId ? [ jobId ] : undefined,
        destinations: destination ? [ destination ] : undefined,
    });

    return {
        status: response.status,
        matrix: response.data.matrix as RoutingMatrix,
    }
}

export const routingDirections = async (req: {stops: LatLng[]}) => {
    const response = await fetchFromServer(Endpoint.routingdirections, req);
    return {
        status: response.status,
        features: response.data.features as GeoJSON.Feature[],
    }
}

export interface WebhookConfig {
    url?: string;
    header?: Record<string, string>;
}

export interface Webhooks {
    job_created?: WebhookConfig;
    job_updated?: WebhookConfig;
    job_completed?: WebhookConfig;
    job_archived?: WebhookConfig;
    job_deleted?: WebhookConfig;
    job_checked?: WebhookConfig;
    place_checked?: WebhookConfig;
    clock_in_out?: WebhookConfig;
    alert_triggered?: WebhookConfig;
}

export const getWebhooks = async () => {
    const response = await fetchFromServer(Endpoint.getwebhooks, {});
    return {
        status: response.status,
        webhooks: response.data.webhooks as Webhooks,
    }
}

export const setWebhooks = async (webhooks: Webhooks) => {
    const response = await fetchFromServer(Endpoint.setwebhooks, webhooks);
    return {
        status: response.status,
        webhooks: response.data.webhooks as Webhooks,
    }
}

export interface ApiKeyInfo {
    id: string;
    type: "legacy" | "rotatable";
    active: boolean;
    key?: string;
    expiresAt: number;
    createdAt: number;
    createdBy: string;
    lastUsedAt: number;
}

export const getApiKeys = async () => {
    const response = await fetchFromServer(Endpoint.getapikeys, {});
    return {
        status: response.status,
        apiKeys: (response.data.apiKeys || []) as ApiKeyInfo[],
    }
}

export const createApiKey = async () => {
    const response = await fetchFromServer(Endpoint.createapikey, { expiresAt: 0 });
    return {
        status: response.status,
        key: response.data.key as string,
        apiKeys: (response.data.apiKeys || []) as ApiKeyInfo[],
    }
}

export const revokeApiKey = async (keyId: string) => {
    const response = await fetchFromServer(Endpoint.revokeapikey, { keyId });
    return {
        status: response.status,
        apiKeys: (response.data.apiKeys || []) as ApiKeyInfo[],
    }
}

export const deleteApiKey = async (keyId: string) => {
    const response = await fetchFromServer(Endpoint.deleteapikey, { keyId });
    return {
        status: response.status,
        apiKeys: (response.data.apiKeys || []) as ApiKeyInfo[],
    }
}

export const getExternalIntegrations = async () => {
    const response = await fetchFromServer(Endpoint.getexternalintegrations, {});
    return {
        status: response.status,
        overview: {
            activeProvider: (response.data.activeProvider || "") as ExternalIntegrationOverview["activeProvider"],
            integrations: (response.data.integrations || []) as ExternalIntegrationOverview["integrations"],
        } as ExternalIntegrationOverview,
    }
}

export const saveExternalIntegrationConfig = async (provider: ExternalIntegrationProvider, config: Record<string, string>) => {
    const response = await fetchFromServer(Endpoint.saveexternalintegrationconfig, { provider, config });
    return {
        status: response.status,
        overview: {
            activeProvider: (response.data.activeProvider || "") as ExternalIntegrationOverview["activeProvider"],
            integrations: (response.data.integrations || []) as ExternalIntegrationOverview["integrations"],
        } as ExternalIntegrationOverview,
    }
}

export const saveExternalIntegrationMappings = async (
    provider: ExternalIntegrationProvider,
    coreMappings: Record<string, string>,
    customFieldMappings: Record<string, string>,
) => {
    const response = await fetchFromServer(Endpoint.saveexternalintegrationmappings, { provider, coreMappings, customFieldMappings });
    return {
        status: response.status,
        overview: {
            activeProvider: (response.data.activeProvider || "") as ExternalIntegrationOverview["activeProvider"],
            integrations: (response.data.integrations || []) as ExternalIntegrationOverview["integrations"],
        } as ExternalIntegrationOverview,
    }
}

export const startExternalIntegrationConnect = async (provider: ExternalIntegrationProvider, options: { mondaySetupToken?: string } = {}) => {
    const response = await fetchFromServer(Endpoint.startexternalintegrationconnect, {
        provider,
        ...(options.mondaySetupToken ? { mondaySetupToken: options.mondaySetupToken } : {}),
    });
    return {
        status: response.status,
        authorizationRequired: response.data.authorizationRequired !== undefined
            ? Boolean(response.data.authorizationRequired)
            : Boolean(response.data.connectUrl),
        connectUrl: response.data.connectUrl as string,
        overview: response.data.overview ? {
            activeProvider: (response.data.overview.activeProvider || "") as ExternalIntegrationOverview["activeProvider"],
            integrations: (response.data.overview.integrations || []) as ExternalIntegrationOverview["integrations"],
        } as ExternalIntegrationOverview : undefined,
    }
}

export const disconnectExternalIntegration = async (provider: ExternalIntegrationProvider) => {
    const response = await fetchFromServer(Endpoint.disconnectexternalintegration, { provider });
    return {
        status: response.status,
        overview: {
            activeProvider: (response.data.activeProvider || "") as ExternalIntegrationOverview["activeProvider"],
            integrations: (response.data.integrations || []) as ExternalIntegrationOverview["integrations"],
        } as ExternalIntegrationOverview,
    }
}

export const reconcileExternalIntegration = async (provider: ExternalIntegrationProvider) => {
    const response = await fetchFromServer(Endpoint.reconcileexternalintegration, { provider });
    return {
        status: response.status,
        overview: {
            activeProvider: (response.data.activeProvider || "") as ExternalIntegrationOverview["activeProvider"],
            integrations: (response.data.integrations || []) as ExternalIntegrationOverview["integrations"],
        } as ExternalIntegrationOverview,
    }
}
